import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Avatar } from 'primereact/avatar';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { ProgressSpinner } from 'primereact/progressspinner';
import { InputSwitch } from 'primereact/inputswitch';
import classNames from 'classnames';
import { useCognitoAuth } from '../contexts/CognitoAuthContext';
import useTenantStore from '../stores/TenantStore';
import { useTenantQuery } from '../hooks/useTenantQuery';
import { useTestUsers } from '../contexts/TestUsersContext';
import { useNotInWhiteListUsers } from '../contexts/NotInWhiteListUsersContext';
import { useNotCustomerUsers } from '../contexts/NotCustomerUsersContext';
import OpenAPI from '../endpoint/Openapi';
import type { components } from '../endpoint/Schema';
import { ReactComponent as ArrowLeftIcon } from '../assets/ic_arrow_left.svg';
import 'primeicons/primeicons.css';

type TenantInfoDto = components['schemas']['TenantInfoDto'];

const TenantSwitcher: React.FC = () => {
  const { user, getIdToken, logout } = useCognitoAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { name: currentTenantName, id: currentTenantId } = useTenantStore();
  const { switchToTenant } = useTenantQuery();
  const { includeTestUsers, setIncludeTestUsers } = useTestUsers();
  const { includeNotInWhiteListUsers, setIncludeNotInWhiteListUsers } =
    useNotInWhiteListUsers();
  const { includeNotCustomerUsers, setIncludeNotCustomerUsers } =
    useNotCustomerUsers();
  const [isOpen, setIsOpen] = useState(false);
  const [tenants, setTenants] = useState<TenantInfoDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [switchingTenant, setSwitchingTenant] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchTenants = async () => {
    if (tenants.length > 0) return; // Already loaded

    try {
      setLoading(true);

      const token = getIdToken();
      if (!token) {
        return;
      }

      // Add a small delay to ensure JWT middleware is properly set up
      await new Promise((resolve) => setTimeout(resolve, 100));

      const response = await OpenAPI.GET(
        '/api/v2/admin/configuration/user-tenants',
      );

      if (response.data) {
        setTenants(response.data);
      }
    } catch (err) {
      // Error loading tenants
    } finally {
      setLoading(false);
    }
  };

  const handleTenantChange = async (tenant: TenantInfoDto) => {
    if (tenant.id === currentTenantId) {
      setIsOpen(false);
      return;
    }

    if (!tenant.id) {
      setIsOpen(false);
      return;
    }

    // Set loading state for this specific tenant
    setSwitchingTenant(tenant.id);

    try {
      // Use the optimized tenant switching with caching
      const success = await switchToTenant(tenant.id);

      if (success) {
        // Update URL with new tenantId
        const newParams = new URLSearchParams(location.search);
        newParams.set('tenantId', tenant.id);
        const newUrl = `${location.pathname}?${newParams.toString()}`;
        navigate(newUrl, { replace: true });
      }
    } finally {
      setSwitchingTenant(null);
      setIsOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    setIsOpen(false);
  };

  const handleToggle = () => {
    if (!isOpen) {
      void fetchTenants();
    }
    setIsOpen(!isOpen);
  };

  const getTenantInitials = (name: string) => {
    return name
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <Button
        onClick={handleToggle}
        className="flex align-items-center gap-3 p-3 border-round-xl bg-white-alpha-10 border-1 border-white-alpha-20 hover:bg-white-alpha-20 transition-all transition-duration-200"
        outlined
        text
      >
        {/* User Avatar */}
        <Avatar
          label={user?.email?.charAt(0).toUpperCase() ?? 'U'}
          className="w-2rem h-2rem border-round-lg bg-primary text-white font-semibold text-sm"
          style={{
            background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)',
          }}
        />

        {/* User & Tenant Info */}
        <div className="text-left">
          <div className="text-white font-medium text-lg">
            {currentTenantName ?? 'Select Organization'}
          </div>
          <div className="text-white text-lg max-w-8rem white-space-nowrap overflow-hidden text-overflow-ellipsis">
            {user?.email ?? 'user@email.com'}
          </div>
        </div>

        {/* Chevron - Unicode fallback with SVG primary */}
        <svg
          className={classNames(
            'w-4 h-4 transition-transform duration-200',
            isOpen ? 'rotate-180' : '',
          )}
          fill="none"
          stroke="white"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={3}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </Button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed top-0 left-0 w-full h-full z-40 bg-black-alpha-20"
            onClick={() => {
              setIsOpen(false);
            }}
          />

          {/* Dropdown */}
          <div className="absolute top-full right-0 mt-2 w-[30rem] bg-white rounded-xl shadow-lg  border-gray-100 overflow-hidden z-50">
            {/* Header */}
            <div className="p-4 bg-gray-50 border-bottom-1 border-gray-200">
              <div className="flex align-items-center gap-3">
                <Avatar
                  label={user?.email?.charAt(0).toUpperCase() ?? 'U'}
                  className="w-2rem h-2rem border-round-lg text-white font-semibold"
                  style={{
                    background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)',
                  }}
                />
                <div>
                  <h3 className="font-semibold text-gray-900 m-0">
                    {user?.email ?? 'User'}
                  </h3>
                  <p className="text-gray-600 text-sm m-0">
                    Switch Organization
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="max-h-16rem overflow-auto">
              {loading ? (
                <div className="p-6 text-center">
                  <ProgressSpinner
                    style={{ width: '2rem', height: '2rem' }}
                    strokeWidth="4"
                    className="mb-3"
                  />
                  <p className="text-gray-600 text-sm m-0">
                    Loading organizations...
                  </p>
                </div>
              ) : tenants.length === 0 ? (
                <div className="p-6 text-center">
                  <div className="w-3rem h-3rem bg-gray-100 border-circle flex align-items-center justify-content-center mx-auto mb-3">
                    <i className="pi pi-building text-gray-400 text-xl" />
                  </div>
                  <p className="text-gray-500 text-sm m-0">
                    No organizations found
                  </p>
                </div>
              ) : (
                tenants.map((tenant) => {
                  const tenantColor =
                    tenant.appColors?.find((c) => c.type === 'P400')?.value ??
                    '#3B82F6';
                  const isSelected = tenant.id === currentTenantId;

                  return (
                    <Button
                      key={tenant.id}
                      onClick={() => {
                        void handleTenantChange(tenant);
                      }}
                      className={classNames(
                        'w-full flex align-items-center gap-3 p-4 hover:bg-gray-50 transition-colors transition-duration-150 border-none border-round-none text-left justify-content-start',
                        isSelected
                          ? 'bg-blue-50 border-right-2 border-blue-500'
                          : '',
                      )}
                      text
                    >
                      {/* Tenant Avatar */}
                      <Avatar
                        label={getTenantInitials(tenant.name)}
                        className="w-2rem h-2rem border-round-lg text-white font-medium text-sm"
                        style={{
                          background: `linear-gradient(135deg, ${tenantColor}, ${tenantColor}dd)`,
                        }}
                      />

                      {/* Tenant Info */}
                      <div className="flex-1 text-left">
                        <div className="font-medium text-gray-900 text-sm">
                          {tenant.name}
                        </div>
                        {tenant.description && (
                          <div className="text-gray-500 text-lg">
                            {tenant.description}
                          </div>
                        )}
                      </div>

                      {/* Selected Indicator or Loading */}
                      {switchingTenant === tenant.id ? (
                        <ProgressSpinner
                          style={{ width: '1rem', height: '1rem' }}
                          strokeWidth="4"
                        />
                      ) : isSelected ? (
                        <div className="w-0-5rem h-0-5rem bg-blue-500 border-circle"></div>
                      ) : null}
                    </Button>
                  );
                })
              )}
            </div>

            {/* Footer with Test Users Toggle and Logout */}
            <Divider className="m-0" />

            {/* Test Users Toggle */}
            <div className="p-3 border-bottom-1 border-gray-100">
              <div className="flex align-items-center justify-content-between gap-3">
                <span className="text-gray-700 text-sm">
                  Include Test Users
                </span>
                <InputSwitch
                  checked={includeTestUsers}
                  onChange={(e) => {
                    setIncludeTestUsers(e.value ?? false);
                  }}
                />
              </div>
            </div>

            {/* NotInWhiteList Users Toggle */}
            <div className="p-3 border-bottom-1 border-gray-100">
              <div className="flex align-items-center justify-content-between gap-3">
                <span className="text-gray-700 text-sm">
                  Include NotInWhiteList Users
                </span>
                <InputSwitch
                  checked={includeNotInWhiteListUsers}
                  onChange={(e) => {
                    setIncludeNotInWhiteListUsers(e.value ?? false);
                  }}
                />
              </div>
            </div>

            {/* NotCustomer Users Toggle */}
            <div className="p-3 border-bottom-1 border-gray-100">
              <div className="flex align-items-center justify-content-between gap-3">
                <span className="text-gray-700 text-sm">
                  Include NotCustomer Users
                </span>
                <InputSwitch
                  checked={includeNotCustomerUsers}
                  onChange={(e) => {
                    setIncludeNotCustomerUsers(e.value ?? false);
                  }}
                />
              </div>
            </div>

            {/* Logout Button */}
            <div className="p-3">
              <Button
                onClick={handleLogout}
                className="w-full flex align-items-center justify-content-center gap-2 p-2 text-red-600 hover:bg-red-50 border-round-lg transition-colors transition-duration-150"
                severity="danger"
                text
              >
                <ArrowLeftIcon className="w-1rem h-1rem" />
                <span className="text-lg font-medium">Sign Out</span>
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TenantSwitcher;
